import React, {useState, useEffect, lazy, Suspense} from 'react';
import './App.css';
import axios from 'axios';

import { Routes, Route, BrowserRouter as Router, useNavigate, Navigate } from 'react-router-dom';

// Components
import UponHeader from './components/UponHeader';
import Banniere from './components/Banniere';
import Footer from './components/Footer';
import Navbar from './components/Navbar';
import SiteIndisponible from './components/SiteIndisponible';
import { useServerDown } from './serverStatus';

// Pages
import {pages, initializeClubPage} from './pages.js';
import Home from './pages/Home';
import { useTranslation } from './i18n';
import { domain, listClubProad, listClubProam, listClubProbd, listClubProbm, PLAYOFFS_VISIBLE, PLAYOFFS_PROA_VISIBLE } from './variables';

// Seule la page d'accueil est dans le bundle principal : les autres pages (et leurs
// dépendances : socket.io, feuilles de match...) sont téléchargées à la première visite.
const Actus = lazy(() => import('./pages/Actus'));
const Docs = lazy(() => import('./pages/Docs'));
const Live = lazy(() => import('./pages/Live'));
const PlayOffDown = lazy(() => import('./pages/PlayOffDown'));
const PlayOffProA = lazy(() => import('./pages/PlayOffProA'));

// Les anciennes URL utilisaient un HashRouter (ex: /pro/#/proad/classement).
// On redirige silencieusement vers l'URL équivalente sans # pour conserver
// les liens déjà partagés/indexés.
function HashRedirect() {
    const navigate = useNavigate();

    useEffect(() => {
        const hash = window.location.hash;
        if (hash.startsWith('#/')) {
            navigate(hash.slice(1), { replace: true });
        } else if (hash === '#') {
            navigate('/', { replace: true });
        }
    }, []);

    return null;
}

function App() {
    const { t } = useTranslation();
    const [showPage, setShowPage] = useState(true);
    const [clubLoaded, setClubLoaded] = useState(false);
    const serverDown = useServerDown();

    useEffect(() => {
        if (clubLoaded === false) {
            getClubs()
        }
    }, [])

    const getClubs = async () => {
      const link = domain + '/server/getAllClubs.php';
      await axios.post(
          link, 
          {
              
          }
      ).then(res => {
          const data = res.data;
          parseClubs(data);

      }).catch(() => {
          // erreur 50x : la page "site indisponible" est affichée (cf. serverStatus.js).
          // Autre échec (ex: 429 persistant malgré les nouvelles tentatives) : on affiche
          // quand même les pages plutôt qu'un site vide ; seules les fiches club manqueront.
          setClubLoaded(true);
      })
    }

    const parseClubs = (data) => {
        data.forEach((item) => {
            const sanitize = (value) => {
                if (!value) return '';
                return String(value)
                  .toLowerCase()
                  .normalize('NFD')
                  .replace(/\p{Diacritic}+/gu, '')
                  .replace(/[^a-z0-9]+/g, '-')
                  .replace(/^-+|-+$/g, '');
            }

            const baseSlug = sanitize(item.identifier) || sanitize(item.short) || sanitize(item.libelleClub) || 'club';
            const uniqueSlug = `${baseSlug}-${item.idClub}`;

            const obj = {
                id: item.idClub,
                identifier: item.identifier,
                title: item.libelleClub,
                short: uniqueSlug,
                slug: uniqueSlug,
                salle: item.libelle_salle,
                adresse: item.adresse_salle,
                ville: item.ville_salle,
                cp: item.cp_salle,
                photo: item.photoClub,
                telephone: item.telephoneClub,
                url: item.siteClub,
                photoSalle: item.photoSalle,
            }
            switch (item.championnat) {
              case 'proam':
                  listClubProam.push(obj);
                  break;
              case 'probm':
                  listClubProbm.push(obj);
                  break;
              case 'proad':
                  listClubProad.push(obj);
                  break;
              case 'probd':
                  listClubProbd.push(obj);
                  break;
              default:
                  break;
            }
        })
        initializeClubPage()
        setClubLoaded(true);
    } 

    // Sous-menu d'une division. Entrée "Playoffs" activée dans server/config/env.json
    // (pas de playoffs en Pro B Dames)
    const getUnderNav = (withPlayoffs) => [
        // Page "Présentation" vide pour l'instant -> masquée du menu (route conservée)
        // {id: 1, title: 'PRESENTATION', redirection: '/presentation'},
        {id: 2, title: t('nav.clubs'), redirection: '/joueurs'},
        {id: 3, title: t('nav.calendar'), redirection: '/calendrier'},
        {id: 5, title: t('nav.standings'), redirection: '/classement'},
        ...(withPlayoffs ? [{id: 4, title: t('nav.playoffs'), redirection: '/playoffs'}] : []),
        {id: 7, title: t('nav.statistics'), redirection: '/statistiques'},
        {id: 8, title: t('nav.palmares'), redirection: '/palmares'},
        {id: 6, title: t('nav.contacts'), redirection: '/clubs'},
    ]

    // Navbar's elements
    const elements = [
        {id: 1, title: t('champ.proam').toUpperCase(), short: 'proam', underNav: getUnderNav(PLAYOFFS_PROA_VISIBLE), redirection: ''},
        {id: 2, title: t('champ.proad').toUpperCase(), short: 'proad', underNav: getUnderNav(PLAYOFFS_VISIBLE), redirection: ''},
        {id: 3, title: t('champ.probm').toUpperCase(), short: 'probm', underNav: getUnderNav(PLAYOFFS_VISIBLE), redirection: ''},
        {id: 4, title: t('champ.probd').toUpperCase(), short: 'probd', underNav: getUnderNav(false), redirection: ''},
        {id: 5, title: t('nav.news'), short: 'actus', underNav: null, redirection: '/actus'},
        {id: 6, title: t('nav.live'), short: 'live', underNav: null, redirection: '/live'},
        {id: 7, title: t('nav.docs'), short: 'docs', underNav: null, redirection: '/docs'},
    ];

  // Erreur 50x du serveur : le site entier est remplacé par la page d'indisponibilité
  if (serverDown) {
    return <SiteIndisponible />;
  }

  return (
    <div className="App">
      
        <Router basename="/pro">
          <HashRedirect />
          <div className="content-wrapper">
              {/* UPON HEADER */}
              <UponHeader />
              {/* NAVBAR */}
              <Banniere />
              <Navbar elements={elements} setShowPage={setShowPage}/>
              {
                showPage && clubLoaded &&
                // hauteur réservée pendant le téléchargement d'une page, pour que le footer ne saute pas
                <Suspense fallback={<div style={{minHeight: '100vh'}} />}>
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/actus" element={<Actus />} />
                    <Route path="/docs" element={<Docs />} />
                    <Route path="/live" element={<Live />} />
                    {/* Ancienne page supprimée (performances affichées dans la fiche joueur) : on renvoie les anciens liens vers l'accueil */}
                    <Route path="/performances" element={<Navigate to="/" replace />} />
                    <Route path="/proam/playoffs" element={<PlayOffProA championnat="proam" />} />
                    <Route path="/probm/playoffs" element={<PlayOffDown championnat="probm" />} />
                    <Route path="/proad/playoffs" element={<PlayOffDown championnat="proad" />} />
                    <Route path="/playoffs/proam" element={<PlayOffProA championnat="proam" />} />
                    <Route path="/playoffs/probm" element={<PlayOffDown championnat="probm" />} />
                    <Route path="/playoffs/proad" element={<PlayOffDown championnat="proad" />} />
                    {/* Pages division */}
                    {
                        pages.map((item) => {
                            return (
                                item.page.map((page, index) => {
                                    return (
                                        <Route key={index} path={page.path} element={page.component} />
                                    )
                                })
                            )
                        })
                    }
                    {/* <Route path="*" element={<Home />} /> */}
                </Routes>
                </Suspense>
              }
            </div>
            {/* FOOTER */}
            <Footer />
          </Router>
    </div>
  );
}

export default App;
